import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sprout } from "lucide-react";
import Seo from "../components/Seo";
import logo from "../assets/logo-light.png";

const storyImg = "https://images.unsplash.com/photo-1693414854278-6b3703411629?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxoYW5kcyUyMHBsYW50aW5nJTIwc2VlZGxpbmclMjBpbiUyMHNvaWx8ZW58MHx8fHwxNzgxNDYyNzYxfDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const [form, setForm] = useState({ name: "", email: "", seed: "", notes: "", website: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.seed) return;
    setLoading(true);
    try {
      await api.post("/seed-requests", form);
      toast.success("Request received. We'll do our best!");
      setForm({ name: "", email: "", seed: "", notes: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="about-page">
      <Seo
        title="Our Story"
        description="Rooted in Amador is a small family seed shop in California's gold country, selling naturally sourced, non-GMO seeds at $3.50 a packet. Read our story."
        path="/about"
      />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-col-reverse lg:flex-row lg:items-center gap-10">
          <div className="flex-1">
            <div className="text-xs tracking-wider-2 uppercase text-sage mb-4">Our story</div>
            <h1 className="font-display text-5xl sm:text-6xl text-darkRoast leading-tight">
              Rooted in the foothills. Run by a family who calls them home.
            </h1>
          </div>
          <img
            src={logo}
            alt="Rooted in Amador, Natural Seed Shop logo"
            className="w-44 h-44 lg:w-56 lg:h-56 object-contain shrink-0 mx-auto lg:mx-0"
            data-testid="about-logo"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-12 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="lg:col-span-7 aspect-[4/5] overflow-hidden rounded-sm border border-darkRoast/10">
          <img src={storyImg} alt="Hands in soil" className="w-full h-full object-cover" />
        </div>
        <div className="lg:col-span-5 space-y-6 text-earth leading-relaxed">
          <p className="font-display text-3xl text-darkRoast leading-snug">
            Rooted in Amador is a small family seed shop in California's gold country.
          </p>
          <p>
            Our roots in these oak-studded hills run more than 30 years deep.
            When we welcomed our baby last year, we made Amador County our family's home,
            and putting down roots of our own in this soil is what inspired this shop.
          </p>
          <p>
            We started Rooted in Amador for a simple reason: organic seed packets this 
            size often cost <strong className="text-darkRoast font-medium">$5 or more</strong>, and that's a steep barrier for families and
            growers who just want to grow real food. We sell every packet for <strong className="text-darkRoast font-medium">$3.50</strong>, the same
            seed quality without the markup.
          </p>
          <p>
            So much of what fills the American grocery aisle is sprayed, processed, and
            stripped of anything real. We think the simplest way back is a seed and a patch 
            of dirt, and our hope is to make that first step easy enough that anyone can take it.
          </p>
          <p>
            We aren't individually certified organic, so we can't legally use that word in our
            product names, but every variety we sell comes from a trusted organic 
            supplier and is non-GMO. We pack each one by hand, right here in Amador.
          </p>
        </div>
      </div>

      {/* Values */}
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 grid sm:grid-cols-3 gap-8 text-center">
        {[
          { k: "Non-GMO, Always", v: "From an organic supplier" },
          { k: "Heirloom & Open-Pollinated", v: "Save seeds, grow again" },
          { k: "Affordable", v: "Every packet $3.50" },
        ].map((b) => (
          <div key={b.k} className="border-t border-darkRoast/10 pt-6">
            <div className="text-xs tracking-wider-2 uppercase text-sage mb-2">{b.k}</div>
            <div className="font-display text-2xl text-darkRoast">{b.v}</div>
          </div>
        ))}
      </div>

      {/* Why it matters */}
      <div className="bg-surface py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Why seeds matter</div>
          <h2 className="font-display text-4xl text-darkRoast mb-8 max-w-2xl">A small act of resistance against industrial food.</h2>
          <div className="grid md:grid-cols-2 gap-10 text-earth leading-relaxed">
            <div>
              <p>
                Most of the food in American grocery stores is grown with synthetic
                pesticides, herbicides, and fungicides. Many varieties are bred for shelf
                life and shipping, not flavor or nutrition. Genetically modified crops
                dominate the corn, soy, and produce supply.
              </p>
              <p className="mt-4">
                Growing your own from non-GMO, organically sourced seeds is one of the
                most direct ways to take back control of what your family eats.
              </p>
            </div>
            <div>
              <p>
                Our seeds are open-pollinated heirloom varieties wherever possible, the
                kind your great-grandparents would have planted. Save the seed at the
                end of the season, and grow it again next year. That's how gardens used
                to work, and that's how we think they should still work.
              </p>
              <p className="mt-4">
                Plant something real. Eat what you grow. Pass the seed on.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Seed Request */}
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24" data-testid="seed-request-section">
        <div className="flex items-center gap-3 mb-4">
          <Sprout className="w-6 h-6 text-sage" strokeWidth={1.5} />
          <div className="text-xs tracking-wider-2 uppercase text-sage">Request a seed</div>
        </div>
        <h2 className="font-display text-4xl text-darkRoast mb-4">Don't see what you're looking for?</h2>
        <p className="text-earth leading-relaxed mb-2">
          Our inventory is small and growing. If there's a variety you want, send us a
          note. We'll do our best to source it.
        </p>
        <p className="text-sm text-terracotta italic mb-8">
          We can't promise anything, but we'll try to make it happen.
        </p>

        <form onSubmit={submit} className="space-y-5 bg-surface p-8 rounded-sm" data-testid="seed-request-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-wider-2 uppercase text-earth">Your name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="req-name" />
            </div>
            <div>
              <label className="text-xs tracking-wider-2 uppercase text-earth">Email *</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="req-email" />
            </div>
          </div>
          <div>
            <label className="text-xs tracking-wider-2 uppercase text-earth">Seed you're looking for *</label>
            <input required value={form.seed} onChange={(e) => setForm({ ...form, seed: e.target.value })} placeholder="e.g., Cherokee Purple Tomato" className="w-full bg-transparent border-b border-darkRoast/20 py-2 focus:outline-none focus:border-sage" data-testid="req-seed" />
          </div>
          <div>
            <label className="text-xs tracking-wider-2 uppercase text-earth">Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else we should know?" className="w-full bg-transparent border border-darkRoast/20 p-2 focus:outline-none focus:border-sage rounded-sm" data-testid="req-notes" />
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
          <button disabled={loading} className="w-full bg-darkRoast text-cream py-3.5 rounded-full text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-60" data-testid="req-submit">
            {loading ? "Sending…" : "Send request"}
          </button>
        </form>
      </div>
    </div>
  );
}
